import { cva, type VariantProps } from 'class-variance-authority';
import { centerRecipe } from './layout.recipe';
import { uiMarker, uiMarkerLayout } from './shared';

/**
 * FEEDBACK — spinner and inline alerts.
 *
 * The two ends of "something is happening": a spinner while you wait, an alert
 * once there is something to say about it.
 */

/**
 * Busy indicator.
 *
 * Sized from the icon scale rather than the text scale, so it matches whatever
 * icon it stands in for — a spinner replacing a 16px icon should be 16px.
 */
export const spinnerRecipe = cva([uiMarker, 'animate-spin shrink-0'], {
  variants: {
    size: {
      xs: 'size-icon-xs',
      sm: 'size-icon-sm',
      md: 'size-icon-md',
      lg: 'size-icon-lg',
      xl: 'size-icon-xl',
    },
    tone: {
      /** Takes the colour of surrounding text. The usual choice. */
      inherit: '',
      muted: 'text-muted-foreground',
      primary: 'text-primary',
      'on-primary': 'text-primary-foreground',
    },
  },
  defaultVariants: { size: 'sm', tone: 'inherit' },
});

export type SpinnerRecipeProps = VariantProps<typeof spinnerRecipe>;

/** Centres a spinner in the space it has, for full-panel loading states. */
/**
 * Loading is Center with a spinner in it, so it uses Center's recipe rather
 * than a second copy of the same three variants. The only difference is the
 * default padding: a bare Center should take no space it was not given, while
 * a loading panel needs room to look deliberate rather than broken.
 */
export const spinnerCenterRecipe: typeof centerRecipe = (props) =>
  centerRecipe({ pad: 'md', ...props });

/**
 * INLINE ALERT
 *
 * A message attached to the page, not floating over it. Use a toast for
 * something transient, an alert for a condition that persists — "this impound
 * has no release form on file".
 */
export const alertRecipe = cva(
  [uiMarker, 'flex w-full items-start gap-3 rounded-lg border p-4'],
  {
    variants: {
      tone: {
        neutral: 'border-border bg-muted text-foreground',
        info: 'border-info/25 bg-info-subtle text-info-subtle-foreground',
        success:
          'border-success/25 bg-success-subtle text-success-subtle-foreground',
        warning:
          'border-warning/25 bg-warning-subtle text-warning-subtle-foreground',
        destructive:
          'border-destructive/25 bg-destructive-subtle text-destructive-subtle-foreground',
      },
    },
    defaultVariants: { tone: 'neutral' },
  }
);

export type AlertRecipeProps = VariantProps<typeof alertRecipe>;

export const alertIconRecipe = cva([uiMarker, 'mt-0.5 size-icon-md shrink-0']);
export const alertTitleRecipe = cva([uiMarker, 'font-sans text-label']);
export const alertBodyRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'font-sans text-body-sm [&_a]:underline',
]);

/**
 * SKELETON
 *
 * A grey block the shape of the thing that is loading. The app drew ~50 of
 * these by hand across five files and disagreed on one thing only: whether it
 * should pulse.
 *
 * Still by default. A block that throbs pulls the eye to the one part of the
 * screen with nothing in it, and for anyone with a motion sensitivity it is
 * the worst thing on the page — the analytics skeleton says so in its own
 * docstring. `animate` is there for the two auction lists that want it, and
 * even then it respects reduced-motion.
 *
 * Size is the caller's: a skeleton is the width of what it stands in for, and
 * that is not on any scale. `className="h-4 w-24"` is the expected spelling.
 */
export const skeletonRecipe = cva([uiMarker, 'shrink-0'], {
  variants: {
    shape: {
      /** A box — an image, a card, a chart. */
      rect: 'rounded-md',
      /** A line of text. Takes the text's height; give it a width. */
      text: 'h-4 w-full rounded',
      /** An avatar or a status dot. Give it one size, e.g. `size-10`. */
      circle: 'rounded-full',
      /** A badge or a chip. */
      pill: 'rounded-pill',
      /** An icon tile or a small card: the larger corner those carry. */
      tile: 'rounded-lg',
    },
    /**
     * How dark the block is. The studio skeleton draws a hierarchy out of
     * two greys — the label's line darker than the hint's under it — and a
     * primary tint where a primary control will land, so the swap to the
     * real screen changes nothing but the content.
     */
    tone: {
      default: 'bg-muted',
      /** A second line, a hint, a value: lighter than the label above it. */
      subtle: 'bg-muted/70',
      /** Where a primary button or the brand mark will be. */
      primary: 'bg-primary/20',
      /** On a muted surface — a band, a read-only box — where `bg-muted` would vanish into it. */
      contrast: 'bg-muted-foreground/20',
    },
    animate: {
      true: 'animate-pulse motion-reduce:animate-none',
      false: '',
    },
  },
  defaultVariants: { shape: 'rect', tone: 'default', animate: false },
});

export type SkeletonRecipeProps = VariantProps<typeof skeletonRecipe>;

/**
 * The light sweep across a loading screen — a band that glides over the
 * frame, invisible on the ground and a shimmer where it crosses the blocks.
 * Absolute, so the parent is the frame: it must be positioned and clip its
 * overflow, which StudioLayout already is and does. The keyframes are in
 * tokens.css; reduced motion hides it there.
 *
 * `via-background/55` rather than white: on the light theme that is white,
 * and on the dark one it is the dark ground, so the band lightens the blocks
 * without flashing a white bar across a dark screen.
 */
export const skeletonSweepRecipe = cva([
  uiMarker,
  'pointer-events-none absolute inset-y-0 -left-1/2 z-10 w-1/2',
  'bg-gradient-to-r from-transparent via-background/55 to-transparent',
  'animate-skeleton-sweep',
]);
